/**
 * ProposalsTab — Proposal history for a business in BusinessWorkspace.
 *
 * Phase 13: Full lifecycle management:
 *   - Status-based contextual actions per proposal
 *   - Version labels (v2, v3, ...)
 *   - Follow-up badge on sent proposals
 *   - "New proposal" CTA navigates to the offer flow.
 */
import React, { useEffect, useState } from 'react';
import {
  FileText, Plus, Calendar, DollarSign, Video, Clock,
  Send, Copy, Edit2, XCircle, CircleCheck, TriangleAlert,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import ProposalStatusBadge from '@/components/deal/ProposalStatusBadge';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';
import { formatDollars } from '@/components/deal/mockDealData';

/**
 * Get contextual actions for a proposal based on its status.
 */
function getActions(proposal) {
  switch (proposal.status) {
    case 'draft':
      return [
        { key: 'edit', label: 'Continue editing', icon: Edit2, handler: 'onEditProposal' },
        { key: 'send', label: 'Send proposal', icon: Send, handler: 'onUpdateStatus', statusArg: 'sent' },
      ];
    case 'sent':
      return [
        { key: 'accept', label: 'Record accepted', icon: CircleCheck, handler: 'onUpdateStatus', statusArg: 'accepted' },
        { key: 'changes', label: 'Record changes', icon: TriangleAlert, handler: 'onUpdateStatus', statusArg: 'changes_requested' },
        { key: 'followup', label: 'Follow up', icon: Send, handler: 'onFollowUp' },
        { key: 'lost', label: 'Not moving forward', icon: XCircle, handler: 'onUpdateStatus', statusArg: 'lost' },
      ];
    case 'changes_requested':
      return [
        { key: 'revise', label: 'Revise proposal', icon: Edit2, handler: 'onRevise' },
      ];
    case 'accepted':
      return proposal.commercial_locked_at
        ? []
        : [
          { key: 'clone', label: 'Create revision', icon: Copy, handler: 'onCloneAsNew' },
        ];
    case 'expired':
    case 'lost':
      return [];
    default:
      return [];
  }
}

export default function ProposalsTab({
  proposals = [],
  creatives = [],
  onNewProposal,
  onViewProposal,
  onEditProposal,
  onFollowUp,
  onRevise,
  onCloneAsNew,
  onUpdateStatus,
}) {
  const [failedThumbnails, setFailedThumbnails] = useState({});
  const visibleProposals = [...proposals]
    .filter((proposal) => proposal.status !== 'deleted')
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());

  useEffect(() => {
    setFailedThumbnails({});
  }, [creatives, proposals]);

  if (visibleProposals.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={FileText}
          title="No proposals yet"
          description="Build an offer to create your first proposal."
        />
        {onNewProposal && (
          <div className="flex justify-center">
            <Button onClick={onNewProposal} className="bg-coral hover:bg-coral/90 text-white">
              <Plus className="h-4 w-4 mr-1" />
              Build a proposal
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Handler dispatcher
  const dispatch = (action, proposal) => {
    const handlers = { onEditProposal, onFollowUp, onRevise, onCloneAsNew, onUpdateStatus };
    const fn = handlers[action.handler];
    if (fn) {
      if (action.statusArg) fn(proposal, action.statusArg);
      else fn(proposal);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          {visibleProposals.length} proposal{visibleProposals.length !== 1 ? 's' : ''}
        </h3>
        {onNewProposal && (
          <Button variant="outline" size="sm" onClick={onNewProposal}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            New proposal
          </Button>
        )}
      </div>

      <div className="space-y-3">
        {visibleProposals.map((proposal) => {
          const linkedCreative = proposal.creativeId
            ? creatives.find((c) => c.id === proposal.creativeId)
            : null;
          const actions = getActions(proposal).filter((action) => {
            if (action.handler === 'onUpdateStatus') return !!onUpdateStatus;
            if (action.handler === 'onEditProposal') return !!onEditProposal;
            if (action.handler === 'onFollowUp') return !!onFollowUp;
            if (action.handler === 'onRevise') return !!onRevise;
            if (action.handler === 'onCloneAsNew') return !!onCloneAsNew;
            return true;
          });

          return (
            <Card
              key={proposal.id}
              className="hover:border-muted-foreground/30 transition-all"
            >
              <CardContent className="p-4">
                {/* Main row — clickable to view */}
                <div
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() => onViewProposal?.(proposal)}
                >
                  {/* Creative thumbnail */}
                  {linkedCreative && (
                    <div className="w-14 h-10 rounded-md overflow-hidden bg-slate-900 shrink-0">
                      {linkedCreative.thumbnailUrl && !failedThumbnails[linkedCreative.id] ? (
                        <img
                          src={linkedCreative.thumbnailUrl}
                          alt="Creative"
                          className="w-full h-full object-cover opacity-80"
                          onError={() => {
                            setFailedThumbnails((prev) => ({ ...prev, [linkedCreative.id]: true }));
                          }}
                        />
                      ) : (
                        <CreativePreviewFallback compact label="Preview unavailable" />
                      )}
                    </div>
                  )}

                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {proposal.packageName || 'Campaign Proposal'}
                      </p>
                      <ProposalStatusBadge status={proposal.status} />
                      {/* Version label */}
                      {proposal.version > 1 && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-100 text-slate-600">
                          v{proposal.version}
                        </Badge>
                      )}
                      {/* Follow-up badge */}
                    {proposal.status === 'sent' && proposal.followUpDays && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">
                        <Clock className="h-2.5 w-2.5 mr-0.5" />
                        Follow up in {proposal.followUpDays}d
                      </Badge>
                    )}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {formatDollars(proposal.price || proposal.budget_cap)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {proposal.flight?.weeks || '—'} weeks
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {proposal.created_at
                      ? new Date(proposal.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : ''}
                  </p>
                </div>

                {/* Status-based actions */}
                {actions.length > 0 && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/50">
                    {actions.map((action) => {
                      const Icon = action.icon;
                      return (
                        <Button
                          key={action.key}
                          variant={action.variant === 'destructive' ? 'outline' : 'outline'}
                          size="sm"
                          className={`text-xs ${
                            action.variant === 'destructive'
                              ? 'border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700'
                              : ''
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(action, proposal);
                          }}
                        >
                          <Icon className="h-3 w-3 mr-1" />
                          {action.label}
                        </Button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
